import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { GoogleSignIn } from "./auth/GoogleSignIn";
import { DevLoginForm } from "./auth/DevLoginForm";
import { ReportingPage } from "./pages/ReportingPage";
import { TeamPage } from "./pages/TeamPage";
import { AdminPage } from "./pages/AdminPage";
import { HistoryPage } from "./pages/HistoryPage";
import { OnboardingNotice } from "./components/OnboardingNotice";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { useT } from "./i18n/I18nContext";

export function App() {
  const { user, loading, signOut } = useAuth();
  const t = useT();

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center text-text-secondary">
        {t("app.loading")}
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  const isManager = user.role === "MANAGER" || user.role === "ADMIN";
  const isAdmin = user.role === "ADMIN";

  const roleLabel =
    user.role === "ADMIN"
      ? t("role.admin")
      : user.role === "MANAGER"
        ? t("role.manager")
        : t("role.employee");

  const navItems: { to: string; end?: boolean; label: string }[] = [
    { to: "/", end: true, label: t("nav.myMonth") },
    { to: "/history", label: t("nav.history") },
    ...(isManager
      ? [{ to: "/team", label: isAdmin ? t("nav.allReports") : t("nav.team") }]
      : []),
    ...(isAdmin ? [{ to: "/admin", label: t("nav.admin") }] : []),
  ];

  return (
    <div className="min-h-screen bg-surface-alt">
      <header className="bg-portal-prussian text-white shadow-md">
        {/* Desktop layout (md+) — unchanged from original design */}
        <div className="mx-auto hidden max-w-7xl items-center justify-between gap-4 px-6 py-3.5 md:flex">
          <div className="flex items-center gap-3.5">
            <img
              src="/agria-logo-header.svg"
              alt="Agria"
              className="h-8 w-auto opacity-95"
              width={96}
              height={32}
            />
            <span className="h-8 w-px shrink-0 bg-white/25" aria-hidden />
            <div className="leading-tight">
              <h1 className="text-base font-semibold tracking-tight text-white">
                {t("app.title")}
              </h1>
              <p className="text-xs font-normal text-white/70">
                {t("app.subtitle")}
              </p>
            </div>
          </div>
          <nav className="flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={navLinkClass}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="header" />
            <div className="hidden text-right text-xs sm:block">
              <div className="font-medium text-white">{user.name}</div>
              <div className="text-white/70">
                {roleLabel}
                {user.employeeId ? ` · ${user.employeeId}` : ""}
              </div>
            </div>
            <button
              onClick={signOut}
              className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/10"
            >
              {t("app.signOut")}
            </button>
          </div>
        </div>

        {/* Mobile layout (<md) — compact top row + scrollable nav strip */}
        <div className="md:hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-2.5">
              <img
                src="/agria-logo-header.svg"
                alt="Agria"
                className="h-7 w-auto opacity-95"
                width={84}
                height={28}
              />
              <span className="h-6 w-px shrink-0 bg-white/25" aria-hidden />
              <h1 className="truncate text-sm font-semibold tracking-tight text-white">
                {t("app.title")}
              </h1>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <LanguageSwitcher variant="header" />
              <button
                onClick={signOut}
                className="rounded-lg border border-white/20 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-white/10"
                aria-label={t("app.signOut")}
                title={t("app.signOut")}
              >
                {t("app.signOut")}
              </button>
            </div>
          </div>
          <nav
            className="flex gap-1 overflow-x-auto border-t border-white/10 px-3 py-2"
            aria-label="Primary"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={mobileNavLinkClass}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-8">
        {!user.employeeId && user.role === "EMPLOYEE" && <OnboardingNotice />}
        <Routes>
          <Route path="/" element={<ReportingPage />} />
          <Route path="/history" element={<HistoryPage />} />
          {isManager && <Route path="/team" element={<TeamPage />} />}
          {isAdmin && <Route path="/admin" element={<AdminPage />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function navLinkClass({ isActive }: { isActive: boolean }) {
  return [
    "rounded-lg px-3 py-1.5 text-sm font-medium transition",
    isActive
      ? "bg-white text-portal-primary"
      : "text-white/85 hover:bg-white/10 hover:text-white",
  ].join(" ");
}

function mobileNavLinkClass({ isActive }: { isActive: boolean }) {
  return [
    "shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition",
    isActive
      ? "bg-white text-portal-primary"
      : "text-white/85 hover:bg-white/10 hover:text-white",
  ].join(" ");
}

function LoginScreen() {
  const t = useT();
  return (
    <div className="grid min-h-screen place-items-center bg-gradient-to-br from-portal-surface-tint via-white to-surface-alt px-4">
      <div className="w-full max-w-md">
        <div className="card p-8 text-center">
          <div className="mb-3 flex justify-end">
            <LanguageSwitcher variant="light" />
          </div>
          <img
            src="/agria-logo.svg"
            alt="Agria"
            className="mx-auto mb-4 h-10 w-auto"
            style={{ filter: "brightness(0)" }}
          />
          <h1 className="font-display text-2xl text-portal-primary">
            {t("app.title")}
          </h1>
          <p className="mt-2 text-sm text-text-secondary">
            {t("login.subtitle")}
          </p>
          <div className="mt-7 flex justify-center">
            <GoogleSignIn />
          </div>
          <DevLoginForm />
          <p className="mt-6 text-xs text-text-muted">{t("login.access")}</p>
        </div>
      </div>
    </div>
  );
}
